import {
  CapitalDepositStatus,
  CompanyLegalBeneficiaryOnboardingStatus,
  OutstandingAgreementType,
} from '__generated__/GQL';
import assertCustomGuardValue from 'common/bento/lib/assertCustomGuardValue';
import getIsAgreementCompleted from 'common/bento/modules/Agreement/libs/getIsAgreementCompleted';
import { MachineGuards } from 'common/bento/types/MachineConfig';
import getDraftStatutesStatus from 'features/Onboarding/modules/CapitalDepositDraftStatutes/libs/getDraftStatutesModuleStatus';
import getCompanyComplianceModuleStatus, {
  CompanyComplianceModuleStatus,
} from 'features/Onboarding/modules/CompanyCompliance/libs/getCompanyComplianceModuleStatus';
import { VerificationStatus } from 'features/Onboarding/modules/Verification/lib/steps/types';

import getPersonalInfoModuleStatus from '../../../modules/PersonalInfo/libs/getPersonalInfoModuleStatus';
import { ApplicantType } from '../../../types';
import { Model } from './model';

export enum Guard {
  IsAwaitingAccountValidation = 'isAwaitingAccountValidation',
  IsApplicationProfileComplete = 'isApplicationProfileComplete',
  ShouldFillCapitalDepositQuestions = 'shouldFillCapitalDepositQuestions',
  shouldFillPersonalInfo = 'shouldFillPersonalInfo',
  ShouldRegisterBeneficiaries = 'shouldRegisterBeneficiaries',
  ShouldFillCompanyComplianceQuestions = 'shouldFillCompanyComplianceQuestions',
  HasCompletedAgreementOfType = 'hasCompletedAgreementOfType',
  IsCapitalDepositPaid = 'isCapitalDepositPaid',
  IsAwaitingBeneficiaries = 'isAwaitingBeneficiaries',
}

const guards: MachineGuards<Model, Guard> = {
  hasCompletedAgreementOfType: ({ getModuleData }, event, { cond }) => {
    assertCustomGuardValue<OutstandingAgreementType>(cond);

    const { outstandingAgreements } = getModuleData().viewer;

    if (!outstandingAgreements) {
      throw new Error(
        `outstandingAgreements required in 'hasCompletedAgreementOfType'`,
      );
    }

    return getIsAgreementCompleted({
      agreementType: cond.value,
      outstandingAgreements,
    });
  },
  isApplicationProfileComplete: ({ getModuleData }) =>
    Boolean(getModuleData().viewer.companyUser?.hasFinishedApplicationSetup),

  isAwaitingAccountValidation: ({ getModuleData }) =>
    Boolean(getModuleData().viewer.companyUser?.isAwaitingAccountValidation),

  isAwaitingBeneficiaries: ({ getModuleData }) => {
    const legalBeneficiaries =
      getModuleData().viewer.company?.legalBeneficiaries;

    if (!legalBeneficiaries) {
      return false;
    }

    return legalBeneficiaries.some(
      ({ isCurrentUser, onboardingStatus }) =>
        !isCurrentUser &&
        [
          CompanyLegalBeneficiaryOnboardingStatus.NotStarted,
          CompanyLegalBeneficiaryOnboardingStatus.InProgress,
          CompanyLegalBeneficiaryOnboardingStatus.Invalid,
        ].includes(onboardingStatus),
    );
  },

  isCapitalDepositPaid: ({ getModuleData }) => {
    return Boolean(getModuleData().viewer.company?.capitalDeposit?.isPaid);
  },

  shouldFillCapitalDepositQuestions: ({ getModuleData }) => {
    const { company, profile } = getModuleData().viewer;

    const capitalDeposit = company?.capitalDeposit;

    if (!capitalDeposit) {
      return false;
    }

    if (
      profile.hasFinishedProfileSetup &&
      capitalDeposit.status === CapitalDepositStatus.Initiated
    ) {
      return true;
    }

    /**
     * Draft statutes are included in the module
     */
    return (
      getDraftStatutesStatus({
        isCapitalDeposit: true,
        isCompanyCreationSASU: false,
        isMainApplicant: true,
        kybDocuments: company.kybDocuments,
      }) === VerificationStatus.ToDo
    );
  },

  shouldFillCompanyComplianceQuestions: ({ getModuleData }) => {
    const { shouldAskSanctionsDeclarationQuestions, viewer } = getModuleData();

    const capitalDeposit = viewer.company?.capitalDeposit;

    if (!viewer.company || !capitalDeposit) {
      return false;
    }

    const companyComplianceModuleStatus = getCompanyComplianceModuleStatus({
      companyProfile: viewer.company.profile,
      shouldAskSanctionsDeclarationQuestions,
    });

    return [
      CompanyComplianceModuleStatus.ToDo,
      CompanyComplianceModuleStatus.Refused,
    ].includes(companyComplianceModuleStatus);
  },

  shouldFillPersonalInfo: ({ getModuleData }) => {
    const { company, profile: userProfile } = getModuleData().viewer;

    if (!company) {
      return false;
    }

    const personalInfoModuleStatus = getPersonalInfoModuleStatus({
      applicantType: ApplicantType.AccountHolder,
      companyLegalBeneficiaries: company.legalBeneficiaries,
      companyProfile: company.profile,
      userProfile,
    });

    return [VerificationStatus.ToDo, VerificationStatus.Invalid].includes(
      personalInfoModuleStatus,
    );
  },

  shouldRegisterBeneficiaries: ({ getModuleData }) => {
    const { company } = getModuleData().viewer;

    if (!company) {
      return false;
    }

    return company.metadata.hasRegisteredAllBeneficiaries === false;
  },
};

export default guards;
